<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import mapboxgl from 'mapbox-gl';
import type { Map, GeoJSONSource, MapMouseEvent } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { Feature, Point, GeoJsonProperties } from 'geojson';

import type { IngredientId } from '@libs/trip-planner-interfaces';

import type { MapboxData, MapboxMarker } from './models';
import { MAPBOX_TOKEN } from './token';

interface MapboxProps {
  data: MapboxData;
  shouldShowZoomControls: boolean;
}

const props = defineProps<MapboxProps>();

interface MapboxEmits {
  (eventName: 'expose-instance', map: Map): void;
  (eventName: 'dragend'): void;
  (eventName: 'zoomend'): void;
  (
    eventName: 'marker-click',
    markerId: {
      hotelCode: string;
      ingredientId: IngredientId;
    }
  ): void;
}

const emit = defineEmits<MapboxEmits>();

const mapContainer = ref<HTMLDivElement | null>(null);
let map: Map | undefined = undefined;

const STATE_COLORS: Record<string, { fill: string; stroke: string }> = {
  normal: { fill: 'white', stroke: '#e4e4e4' },
  selected: { fill: '#462170', stroke: '#e4e4e4' },
  inspected: { fill: '#8b53ef', stroke: '#8b53ef' },
  'selected-inspected': { fill: '#8b53ef', stroke: '#e4e4e4' },
  'previously-seen': { fill: '#fdfdfc', stroke: '#e4e4e4' },
};

const mThreshold = 5;
const lThreshold = 7;

const NON_SELECTED_MARKER_WIDTH_BUCKETS: {
  width: number;
  name: string;
}[] = [
  { width: 60, name: 's' },
  { width: 68, name: 'm' },
  { width: 76, name: 'l' },
];

const selectedMThreshold = 7;
const selectedLThreshold = 9;

const SELECTED_MARKER_WIDTH_BUCKETS: {
  width: number;
  name: string;
}[] = [
  { width: 66, name: 's' },
  { width: 76, name: 'm' },
  { width: 86, name: 'l' },
];

function mapMarkers(
  markers: MapboxMarker[]
): Feature<Point, GeoJsonProperties>[] {
  return markers.reduce(
    (points: Feature<Point, GeoJsonProperties>[], marker: MapboxMarker) => {
      const label =
        marker.state === 'selected' || marker.state === 'selected-inspected'
          ? `✓ ${marker.label}`
          : marker.label;
      const point: Feature<Point, GeoJsonProperties> = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [marker.point.longitude, marker.point.latitude],
        },
        properties: {
          label,
          state: marker.state,
          hotelCode: marker.hotelCode,
          ingredientId: marker.ingredientId,
        },
      };

      if (marker.label) {
        points.push(point);
      }
      return points;
    },
    [] as Feature<Point, GeoJsonProperties>[]
  );
}

function loadStateImages(map: Map): void {
  const height = 32;

  Object.entries(STATE_COLORS).forEach(([state, colors]) => {
    if (state === 'selected' || state === 'selected-inspected') {
      SELECTED_MARKER_WIDTH_BUCKETS.forEach((bucket) => {
        const svg = createPillSVG(
          bucket.width,
          height,
          1,
          colors.fill,
          colors.stroke
        );
        const img = new Image();
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
        img.onload = (): void => {
          const id = `price-pill-${state}-${bucket.name}`;
          if (!map.hasImage(id)) {
            map.addImage(id, img);
          }
        };
      });
    } else {
      NON_SELECTED_MARKER_WIDTH_BUCKETS.forEach((bucket) => {
        const svg = createPillSVG(
          bucket.width,
          height,
          1,
          colors.fill,
          colors.stroke
        );
        const img = new Image();
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
        img.onload = (): void => {
          const id = `price-pill-${state}-${bucket.name}`;
          if (!map.hasImage(id)) {
            map.addImage(id, img);
          }
        };
      });
    }
  });
}

function createPillSVG(
  width = 64,
  height = 32,
  border = 1,
  fill: string,
  stroke: string
): string {
  return `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
        <rect x="0" y="0" width="${width}" height="${height}" rx="${height / 2}" ry="${height / 2}"
              fill="${fill}" stroke="${stroke}" stroke-width="${border}" />
      </svg>
    `;
}

let highlightMarker: mapboxgl.Marker | null = null;

function setHighlightMarker(lng?: number, lat?: number, color?: string): void {
  if (!map) {
    return;
  }

  if (highlightMarker) {
    highlightMarker.remove();
    highlightMarker = null;
  }

  if (lng !== undefined && lat !== undefined) {
    highlightMarker = new mapboxgl.Marker({ color })
      .setLngLat([lng, lat])
      .addTo(map);
  }
}

onMounted(() => {
  mapboxgl.accessToken = MAPBOX_TOKEN;

  if (!mapContainer.value) {
    return;
  }

  const bounds = new mapboxgl.LngLatBounds();

  props.data.markers.forEach((marker) => {
    bounds.extend([marker.point.longitude, marker.point.latitude]);
  });

  map = new mapboxgl.Map({
    container: mapContainer.value,
    zoom: 4,
    maxZoom: 18,
    minZoom: 4,
    style: 'mapbox://styles/mapbox/streets-v12',
    bounds: bounds,
    fitBoundsOptions: { padding: 24 },
  });

  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();

  if (props.shouldShowZoomControls) {
    map.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true }),
      'bottom-right'
    );
  }

  map.on('dragend', () => emit('dragend'));
  map.on('zoomend', () => emit('zoomend'));

  map.on('load', () => {
    if (!map) {
      return;
    }

    loadStateImages(map);

    map.addSource('markers', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: mapMarkers(props.data.markers),
      },
      // NOTE: Options
      cluster: true,
      // 0 - 22
      clusterMaxZoom: 22,
      clusterRadius: 40,
      clusterMinPoints: 2,
    });

    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'markers',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#fff',
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 30, 26],
        'circle-stroke-color': '#e4e4e4',
        'circle-stroke-width': 1,
      },
    });

    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'markers',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 14,
      },
      paint: { 'text-color': '#180924' },
    });

    map.addLayer({
      id: 'prices',
      type: 'symbol',
      source: 'markers',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': [
          'concat',
          [
            'match',
            ['get', 'state'],
            'normal',
            'price-pill-normal-',
            'selected',
            'price-pill-selected-',
            'inspected',
            'price-pill-inspected-',
            'selected-inspected',
            'price-pill-selected-inspected-',
            'previously-seen',
            'price-pill-previously-seen-',
            'price-pill-normal-',
          ],
          [
            'case',
            ['<=', ['length', ['get', 'label']], mThreshold],
            's',
            ['<=', ['length', ['get', 'label']], lThreshold],
            'm',
            'l',
          ],
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': 1,
        'icon-anchor': 'center',
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 14,
        'text-anchor': 'center',
      },
      paint: {
        'text-color': [
          'match',
          ['get', 'state'],
          'normal',
          '#180924',
          'inspected',
          'white',
          'selected',
          'white',
          'selected-inspected',
          'white',
          'previously-seen',
          '#6a6a6a',
          '#180924',
        ],
      },
    });

    map.addSource('selected-marker', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: props.data.selectedMarker
          ? mapMarkers([props.data.selectedMarker])
          : [],
      },
    });

    map.addLayer({
      id: 'selected-marker-layer',
      type: 'symbol',
      source: 'selected-marker',
      layout: {
        'icon-image': [
          'concat',
          [
            'match',
            ['get', 'state'],
            'selected',
            'price-pill-selected-',
            'selected-inspected',
            'price-pill-selected-inspected-',
            'price-pill-selected-',
          ],
          [
            'case',
            ['<=', ['length', ['get', 'label']], selectedMThreshold],
            's',
            ['<=', ['length', ['get', 'label']], selectedLThreshold],
            'm',
            'l',
          ],
        ],
        'icon-size': 1,
        'icon-anchor': 'center',
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 14,
        'text-anchor': 'center',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: {
        'text-color': '#fff',
      },
    });

    // NOTE: Zoom on cluster click
    map.on('click', 'clusters', (event: MapMouseEvent) => {
      if (!map) {
        return;
      }

      const topFeatures = map.queryRenderedFeatures(event.point, {
        layers: ['selected-marker-layer', 'prices'],
      });

      if (topFeatures.length > 0) {
        return;
      }

      const clusterFeatures = map.queryRenderedFeatures(event.point, {
        layers: ['clusters'],
      });
      if (!clusterFeatures.length) {
        return;
      }

      const cluster = clusterFeatures[0];

      if (!cluster) {
        return;
      }

      const clusterId = cluster.properties?.cluster_id as number | undefined;
      const source = map.getSource('markers') as GeoJSONSource;

      if (!source || clusterId === undefined) {
        return;
      }

      const geometry = cluster.geometry;
      if (geometry?.type !== 'Point') {
        return;
      }

      const center = geometry.coordinates as [number, number];

      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err || !map) {
          return;
        }

        map.easeTo({
          center,
          zoom: zoom ?? undefined,
          duration: 800,
        });
      });
    });

    // NOTE: Cursor on clusters
    map.on('mouseenter', 'clusters', () => {
      if (!map) {
        return;
      }
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'clusters', () => {
      if (!map) {
        return;
      }
      map.getCanvas().style.cursor = '';
    });

    map.on('mouseenter', 'prices', () => {
      if (!map) {
        return;
      }
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'prices', () => {
      if (!map) {
        return;
      }
      map.getCanvas().style.cursor = '';
    });

    const lastClickedMarkerId = ref<string | null>(null);

    map.on('click', 'prices', (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature) {
        return;
      }

      const hotelCode = feature.properties?.hotelCode;
      const ingredientId = feature.properties?.ingredientId;

      if (!hotelCode || !ingredientId) {
        return;
      }

      const markerKey = `${hotelCode}-${ingredientId}`;
      if (lastClickedMarkerId.value === markerKey) {
        lastClickedMarkerId.value = null;
      } else {
        lastClickedMarkerId.value = markerKey;
        emit('marker-click', { hotelCode, ingredientId });
      }
    });

    map.on('click', 'selected-marker-layer', (event: MapMouseEvent) => {
      event.originalEvent.stopPropagation();

      const feature = event.features?.[0];
      if (!feature) {
        return;
      }

      const hotelCode = feature.properties?.hotelCode;
      const ingredientId = feature.properties?.ingredientId;
      if (!hotelCode || !ingredientId) {
        return;
      }

      const markerKey = `${hotelCode}-${ingredientId}`;

      if (lastClickedMarkerId.value === markerKey) {
        lastClickedMarkerId.value = null;
      } else {
        lastClickedMarkerId.value = markerKey;
        emit('marker-click', { hotelCode, ingredientId });
      }
    });

    emit('expose-instance', map);
  });
});

watch(
  () => props.data.markers,
  (newMarkers) => {
    if (!map) {
      return;
    }

    const source = map.getSource('markers') as GeoJSONSource;
    if (source) {
      source.setData({
        type: 'FeatureCollection',
        features: mapMarkers(newMarkers),
      });
    }
  },
  { deep: true }
);

watch(
  () => props.data.highlightMarker,
  (marker) => {
    if (!map) {
      return;
    }
    if (marker?.longitude !== undefined && marker?.latitude !== undefined) {
      setHighlightMarker(marker.longitude, marker.latitude, '#8b53ef');
    } else {
      setHighlightMarker();
    }
  },
  { deep: true }
);

watch(
  () => props.data.selectedMarker,
  (marker) => {
    if (!map) {
      return;
    }

    const source = map.getSource('selected-marker') as GeoJSONSource;
    if (!source) {
      return;
    }

    const features = marker ? mapMarkers([marker]) : [];

    source.setData({
      type: 'FeatureCollection',
      features,
    });
  },
  { deep: true }
);
</script>

<template>
  <div class="mapbox">
    <div ref="mapContainer" class="map-container" />
  </div>
</template>

<style lang="scss">
.mapbox {
  position: relative;
  width: 100%;
  height: 100%;

  .map-container {
    width: 100%;
    height: 100%;
  }
}

// NOTE: Focus on map canvas looks bad
.mapboxgl-canvas:focus {
  outline: none;
}
</style>
